import { nowUTC } from "@homebound/activesupport";
import {
  Avatar,
  Button,
  ButtonMenu,
  column,
  dateColumn,
  emptyCell,
  GridColumn,
  Pagination,
  ScrollableContent,
  selectColumn,
  simpleDataRows,
  simpleHeader,
  SimpleHeaderAndData,
  useComputed,
  useGridTableApi,
} from "@homebound/beam";
import { sentenceCase } from "change-case";
import { useMemo, useState } from "react";
import { useHistory, useParams } from "react-router";
import { createChangeEventsUrl, createChangeEventUrl, createProjectProductOfferingConfigApplyUrl } from "src/RouteUrls";
import { dateCell, QueryTable } from "src/components";
import {
  ChangeEventBudgetPhase,
  ChangeEventType,
  Order,
  ProductOfferingConfigOrder,
  ProjectReadyPlanConfigOrientation,
  ReadyPlanType,
  Stage,
} from "src/generated/graphqlTypes";
import useZodQueryString from "src/hooks/useZodQueryString";
import { EmptyState } from "src/routes/components/EmptyState";
import { EmptyPlanAndOptionImage } from "src/routes/developments/plan-and-options/components/EmptyStateSvgs";
import { PageHeader } from "src/routes/layout/PageHeader";
import { ProjectParams } from "src/routes/routesDef";
import { isDefined, pageSchema, queryResult } from "src/utils";
import { parseOrder, toOrder } from "src/utils/ordering";
import { Temporal } from "temporal-polyfill";
import {
  AppliableProductOfferingConfigs_CheckoutConfigFragment,
  ApplicableProductOfferingConfigsPage_StructureFragment,
  useAppliableProductOfferingConfigsApplyWithoutChangesMutation,
  useAppliableProductOfferingConfigsQuery,
  useApplicableProductOfferingConfigsPageStructureMetadataQuery,
} from "./AppliableProductOfferingConfigsPage.generated";

type ConfigDisplay = {
  id: string;
  name: string;
  planName: string;
  source: "Offering" | "Checkout" | "Current Configuration";
  productOffering: { id: string; name: string };
  createdAt: Temporal.ZonedDateTime;
  updatedAt: Temporal.ZonedDateTime;
  createdBy?: { internalUser?: { id: string; name: string; avatar?: string } };
  displayStatus?: string;
  // Checkout-specific fields for "Apply Without Changes"
  checkoutConfig?: AppliableProductOfferingConfigs_CheckoutConfigFragment;
};

type Row = SimpleHeaderAndData<ConfigDisplay>;

export function AppliableProductOfferingConfigsPage() {
  const { projectId } = useParams<ProjectParams>();
  const query = useApplicableProductOfferingConfigsPageStructureMetadataQuery({
    variables: { structureId: projectId },
  });
  return queryResult(query, {
    data: ({ structure, changeEventReasons }) => (
      <AppliableProductOfferingConfigsPageView structure={structure} changeEventReasons={changeEventReasons} />
    ),
  });
}

export type AppliableProductOfferingConfigsPageViewProps = {
  // This shows the view of all Appliable Product Offering Configs to a given Project (Structure)
  structure: ApplicableProductOfferingConfigsPage_StructureFragment;
  changeEventReasons: { id: string; code: string }[];
};

export function AppliableProductOfferingConfigsPageView({
  structure,
  changeEventReasons,
}: AppliableProductOfferingConfigsPageViewProps) {
  const [pageSettings, setPageSettings] = useZodQueryString(pageSchema);
  const [order, setOrder] = useState<ProductOfferingConfigOrder>({ updatedAt: Order.Desc });
  const [applyWithoutChanges] = useAppliableProductOfferingConfigsApplyWithoutChangesMutation();

  const constructionStage = structure.stages.find((s) => s.stage.code === Stage.Construction);

  // For ADU structures, show checkout configs from the main house structure
  const checkoutConfigStructureId = structure.mainHouseStructure?.id ?? structure.id;

  const existingProductOffering =
    structure.readyPlanConfig?.readyPlan?.__typename === "ProductOffering"
      ? structure.readyPlanConfig.readyPlan
      : undefined;

  // If we have an existing config, we can show it as the current configuration
  const currentConfig: ConfigDisplay[] = !!existingProductOffering
    ? [
        {
          source: "Current Configuration",
          id: structure.readyPlanConfig?.id!,
          name: "Current Configuration",
          planName: existingProductOffering.planPackage.name || "(no plan)",
          productOffering: existingProductOffering,
          createdAt: structure.readyPlanConfig?.createdAt || nowUTC(),
          updatedAt: structure.readyPlanConfig?.updatedAt || nowUTC(),
        },
      ]
    : [];

  const appliableConfigsQuery = useAppliableProductOfferingConfigsQuery({
    variables: {
      filter: {
        projects: [structure.parent.parent.parent.id], // We can apply any configs that belong to the COMMUNITY
      },
      order,
      page: pageSettings,
      structureId: checkoutConfigStructureId,
    },
  });
  const { loading, data } = appliableConfigsQuery;
  const productOfferingConfigs = data?.productOfferingConfigs.entities;
  const checkoutConfigs = data?.checkoutConfigs.entities.filter((c) => isDefined(c.productOffering)); // Filter out any CO configs that don't have a PO

  const backUrl = createChangeEventsUrl(structure.id);

  const api = useGridTableApi<Row>();
  const selectedRows = useComputed(() => api.getSelectedRows("data").map((r) => r.data), [api]);

  const disableApply = useMemo(
    () =>
      (selectedRows.isEmpty && "No configurations selected") ||
      (selectedRows.length > 1 && "Must select only one configuration to apply"),
    [selectedRows.isEmpty, selectedRows.length],
  );

  const history = useHistory();
  const subHeader = "Select Configuration to apply to Structure";

  // Combine the PO configs with the checkout configs in a structure that can be displayed in the table
  const configs: ConfigDisplay[] = [
    ...currentConfig,
    ...(checkoutConfigs?.map((coConfig) => {
      const config: ConfigDisplay = {
        source: "Checkout",
        id: coConfig.id,
        name: coConfig.name || "(no name)",
        planName: coConfig.aggregateVersion?.displayName || "(no plan)",
        productOffering: { id: coConfig.productOffering!.id, name: coConfig.productOffering!.name },
        createdAt: coConfig.createdAt,
        updatedAt: coConfig.updatedAt,
        createdBy: { internalUser: coConfig.createdBy?.internalUser || undefined },
        checkoutConfig: coConfig,
        displayStatus: coConfig.status,
      };
      return config;
    }) || []),
    ...(productOfferingConfigs?.map((poConfig) => {
      const config: ConfigDisplay = {
        source: "Offering",
        id: poConfig.id,
        name: poConfig.name,
        planName: poConfig.productOffering.planPackage?.name || "(no plan)",
        productOffering: { id: poConfig.productOffering.id, name: poConfig.productOffering.name },
        createdAt: poConfig.createdAt,
        updatedAt: poConfig.updatedAt,
        createdBy: { internalUser: poConfig.createdBy.internalUser || undefined },
      };
      return config;
    }) || []),
  ];

  return (
    <>
      {!loading && configs?.isEmpty ? (
        <>
          <PageHeader title="Select Configuration" hideTitle backButton={backUrl} left={subHeader} />
          <EmptyState
            message="There are no configurations available for this Structure yet"
            svg={<EmptyPlanAndOptionImage />}
          />
        </>
      ) : (
        <>
          <PageHeader
            title="Select Configuration"
            backButton={backUrl}
            left={subHeader}
            right={
              <ApplyConfigButton
                {...{
                  selectedRows,
                  disableApply,
                  structure,
                  constructionStage,
                  changeEventReasons,
                  applyWithoutChanges,
                  history,
                }}
              />
            }
          />
          <ScrollableContent>
            <QueryTable
              columns={columns}
              api={api}
              rowStyles={{ header: {}, data: {} }}
              query={appliableConfigsQuery}
              createRows={() => [simpleHeader, ...simpleDataRows(configs)]}
              sorting={{
                on: "server",
                onSort: (key, direction) => setOrder(toOrder(key, direction)),
                value: parseOrder(order),
              }}
              stickyHeader
              fallbackMessage="There aren't any configurations to display."
            />
            <Pagination
              page={[pageSettings, setPageSettings]}
              totalCount={data?.productOfferingConfigs.pageInfo.totalCount || 0}
            />
          </ScrollableContent>
        </>
      )}
    </>
  );
}

const columns: GridColumn<Row>[] = [
  selectColumn({ header: emptyCell }),
  column<Row>({ header: "Source", data: ({ source }) => source }),
  column<Row>({ header: "Configuration Name", data: ({ name }) => name }),
  column<Row>({ header: "ID", data: ({ id }) => id }),
  column<Row>({ header: "Status", data: ({ displayStatus }) => (displayStatus ? sentenceCase(displayStatus) : "") }),
  column<Row>({ header: "Plan", data: ({ planName }) => planName }),
  column<Row>({
    header: "Created by",
    data: ({ createdBy }) => (
      <Avatar src={createdBy?.internalUser?.avatar} name={createdBy?.internalUser?.name} showName />
    ),
  }),
  dateColumn<Row>({ header: "Created", data: ({ createdAt }) => dateCell(createdAt) }),
  dateColumn<Row>({ header: "Updated", serverSideSortKey: "updatedAt", data: ({ updatedAt }) => dateCell(updatedAt) }),
];

type ApplyConfigButtonProps = {
  selectedRows: ConfigDisplay[];
  disableApply: string | false;
  structure: ApplicableProductOfferingConfigsPage_StructureFragment;
  constructionStage: ApplicableProductOfferingConfigsPage_StructureFragment["stages"][number] | undefined;
  changeEventReasons: { id: string; code: string }[];
  applyWithoutChanges: ReturnType<typeof useAppliableProductOfferingConfigsApplyWithoutChangesMutation>[0];
  history: ReturnType<typeof useHistory>;
};

function ApplyConfigButton({
  selectedRows,
  disableApply,
  structure,
  constructionStage,
  changeEventReasons,
  applyWithoutChanges,
  history,
}: ApplyConfigButtonProps) {
  const selectedConfig = selectedRows[0];
  const isCheckoutConfig = selectedConfig?.source === "Checkout";
  const hasSignedContract = constructionStage?.hasSignedContract;

  // Show dropdown menu when a checkout config is selected AND there's a signed contract
  const showApplyOptions = isCheckoutConfig && hasSignedContract;

  const handleApplyWithChanges = () => {
    const configId = selectedConfig.id;
    history.push({ pathname: createProjectProductOfferingConfigApplyUrl(structure.id, configId) });
  };

  const handleApplyWithoutChanges = async () => {
    const checkoutConfig = selectedConfig.checkoutConfig!;
    const isAdu = structure.planType?.code === ReadyPlanType.Adu;
    const productOfferingId = isAdu ? checkoutConfig.aduProductOffering!.id : checkoutConfig.productOffering!.id;

    // Determine orientation from checkout config
    const proposedOrientation = checkoutConfig.isReversed
      ? ProjectReadyPlanConfigOrientation.Left
      : ProjectReadyPlanConfigOrientation.Right;

    // Get original ready plan ID if it's a ProductOffering
    const originalReadyPlanId =
      structure.readyPlanConfig?.readyPlan?.__typename === "ProductOffering"
        ? structure.readyPlanConfig.readyPlan.id
        : undefined;

    const result = await applyWithoutChanges({
      variables: {
        input: {
          projectStageId: constructionStage?.id,
          readyPlanId: productOfferingId,
          originalReadyPlanId,
          proposedOrientation,
          checkoutConfigId: checkoutConfig.id,
          title: `Apply Checkout Config ${checkoutConfig.name || checkoutConfig.id} on ${structure.name}`,
          reasonId:
            changeEventReasons.find((reason) => reason.code === "RFP_EXECUTION")?.id ?? changeEventReasons.first!.id,
          type: ChangeEventType.External,
          budgetPhase: ChangeEventBudgetPhase.Prebid,
        },
      },
    });

    const changeEventId = result.data?.saveChangeEvent.changeEvent.id;
    history.push(
      changeEventId ? createChangeEventUrl(structure.id, changeEventId) : createChangeEventsUrl(structure.id),
    );
  };

  if (showApplyOptions) {
    return (
      <ButtonMenu
        trigger={{ label: "Apply Configuration", variant: "primary" }}
        disabled={disableApply}
        items={[
          { label: "Apply With Changes", onClick: handleApplyWithChanges },
          { label: "Apply Without Changes", onClick: handleApplyWithoutChanges },
        ]}
      />
    );
  }

  return (
    <Button label="Apply Configuration" variant="primary" disabled={disableApply} onClick={handleApplyWithChanges} />
  );
}
